import React, { useState, useEffect, useCallback } from 'react';
import { stockAPI } from '../../lib/api';
import { formatDate, formatCurrency, apiErrorMessage } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Package, Plus, Search, AlertTriangle, Edit } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['Tablets', 'Capsules', 'Syrups', 'Injections', 'Ointments', 'Drops', 'Inhalers', 'Medical Supplies', 'Other'];

const StockManagement = () => {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    medicine_name: '',
    generic_name: '',
    category: '',
    manufacturer: '',
    batch_number: '',
    expiry_date: '',
    quantity: 0,
    unit_price: 0,
    reorder_level: 10,
  });

  const fetchStock = useCallback(async () => {
    try {
      const response = await stockAPI.getAll({ low_stock: showLowStock || undefined });
      setStock(response.data);
    } catch (error) {
      toast.error('Failed to load stock');
    } finally {
      setLoading(false);
    }
  }, [showLowStock]);


  useEffect(() => {
    fetchStock();
  }, [showLowStock, fetchStock]);
  const filteredStock = stock.filter(item => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      item.medicine_name?.toLowerCase().includes(searchLower) ||
      item.generic_name?.toLowerCase().includes(searchLower)
    );
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await stockAPI.create(formData);
      toast.success('Stock added successfully');
      setDialogOpen(false);
      setFormData({
        medicine_name: '',
        generic_name: '',
        category: '',
        manufacturer: '',
        batch_number: '',
        expiry_date: '',
        quantity: 0,
        unit_price: 0,
        reorder_level: 10,
      });
      fetchStock();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to add stock'));
    }
  };

  const openEditDialog = (item) => {
    setSelectedItem(item);
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    try {
      await stockAPI.update(selectedItem.id, {
        quantity: selectedItem.quantity,
        unit_price: selectedItem.unit_price,
        reorder_level: selectedItem.reorder_level,
      });
      toast.success('Stock updated');
      setEditDialogOpen(false);
      fetchStock();
    } catch (error) {
      toast.error('Failed to update stock');
    }
  };

  const lowStockCount = stock.filter(s => s.quantity <= s.reorder_level).length;

  return (
    <div data-testid="stock-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Stock Management</h1>
            <p className="text-slate-500">Manage medicine inventory</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="add-stock-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Stock
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-[Manrope]">Add New Stock</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Medicine Name *</Label>
                    <Input
                      value={formData.medicine_name}
                      onChange={(e) => setFormData({...formData, medicine_name: e.target.value})}
                      placeholder="Brand name"
                      required
                      data-testid="input-name"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Generic Name *</Label>
                    <Input
                      value={formData.generic_name}
                      onChange={(e) => setFormData({...formData, generic_name: e.target.value})}
                      placeholder="Generic/salt name"
                      required
                      data-testid="input-generic"
                    />
                  </div>
                  <div>
                    <Label>Category *</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                      <SelectTrigger data-testid="select-category">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Manufacturer *</Label>
                    <Input
                      value={formData.manufacturer}
                      onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                      placeholder="Company name"
                      required
                      data-testid="input-manufacturer"
                    />
                  </div>
                  <div>
                    <Label>Batch Number *</Label>
                    <Input
                      value={formData.batch_number}
                      onChange={(e) => setFormData({...formData, batch_number: e.target.value})}
                      placeholder="Batch no."
                      required
                      data-testid="input-batch"
                    />
                  </div>
                  <div>
                    <Label>Expiry Date *</Label>
                    <Input
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                      required
                      data-testid="input-expiry"
                    />
                  </div>
                  <div>
                    <Label>Quantity *</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 0})}
                      required
                      data-testid="input-quantity"
                    />
                  </div>
                  <div>
                    <Label>Unit Price (₹) *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.unit_price}
                      onChange={(e) => setFormData({...formData, unit_price: parseFloat(e.target.value) || 0})}
                      required
                      data-testid="input-price"
                    />
                  </div>
                  <div>
                    <Label>Reorder Level</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.reorder_level}
                      onChange={(e) => setFormData({...formData, reorder_level: parseInt(e.target.value) || 10})}
                      data-testid="input-reorder"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="submit-stock">Add Stock</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="page-content">
        {/* Low Stock Alert */}
        {lowStockCount > 0 && (
          <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <div>
              <p className="font-medium text-orange-800">Low Stock Alert</p>
              <p className="text-sm text-orange-600">{lowStockCount} items are below reorder level</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => setShowLowStock(!showLowStock)}
            >
              {showLowStock ? 'Show All' : 'Show Low Stock'}
            </Button>
          </div>
        )}

        <Card>
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                <Package className="w-5 h-5 text-red-500" />
                Inventory ({filteredStock.length} items)
              </CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search medicines..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 w-64"
                  data-testid="search-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading stock...</div>
            ) : filteredStock.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No stock found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Medicine</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Batch</TableHead>
                    <TableHead>Expiry</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStock.map((item) => (
                    <TableRow key={item.id} data-testid={`stock-row-${item.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.medicine_name}</p>
                          <p className="text-xs text-slate-500">{item.generic_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell className="font-mono text-sm">{item.batch_number}</TableCell>
                      <TableCell>{formatDate(item.expiry_date)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className={item.quantity <= item.reorder_level ? 'text-orange-600 font-semibold' : ''}>
                            {item.quantity}
                          </span>
                          {item.quantity <= item.reorder_level && (
                            <Badge className="bg-orange-100 text-orange-800 text-xs">Low</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatCurrency(item.unit_price)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(item)}
                          data-testid={`edit-${item.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Update Stock</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4 mt-4">
              <div>
                <p className="text-sm text-slate-500">Medicine</p>
                <p className="font-medium">{selectedItem.medicine_name}</p>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="0"
                  value={selectedItem.quantity}
                  onChange={(e) => setSelectedItem({...selectedItem, quantity: parseInt(e.target.value) || 0})}
                  data-testid="edit-quantity"
                />
              </div>
              <div>
                <Label>Unit Price (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={selectedItem.unit_price}
                  onChange={(e) => setSelectedItem({...selectedItem, unit_price: parseFloat(e.target.value) || 0})}
                  data-testid="edit-price"
                />
              </div>
              <div>
                <Label>Reorder Level</Label>
                <Input
                  type="number"
                  min="0"
                  value={selectedItem.reorder_level}
                  onChange={(e) => setSelectedItem({...selectedItem, reorder_level: parseInt(e.target.value) || 0})}
                  data-testid="edit-reorder"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button className="bg-red-500 hover:bg-red-600" onClick={handleUpdate} data-testid="save-stock">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockManagement;
